import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'

function AuthForm({ onClose }) {
  const [isSignUp, setIsSignUp] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  
  const { login, register, googleSignIn } = useAuth()
  const navigate = useNavigate()

  // Initialize Google Sign-In button when component mounts
  useEffect(() => {
    const initializeGoogleSignIn = async () => {
      try {
        // Wait for Google to be available
        let attempts = 0
        while (!window.google?.accounts?.id && attempts < 50) {
          await new Promise(resolve => setTimeout(resolve, 100))
          attempts++
        }

        if (!window.google?.accounts?.id) {
          // console.warn('Google Identity Services not available')
          return
        }

        // Initialize Google Identity Services
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '58318415240-mfd8ncm157k1lf93mv6each785loorvf.apps.googleusercontent.com',
          callback: async (response) => {
            try {
              // console.log('Google callback received:', response)
              const result = await googleSignIn(response.credential)
              if (result.success) {
                onClose()
              } else {
                setError(result.error)
              }
            } catch (error) {
              // console.error('Google sign-in callback error:', error)
              setError('Google sign-in failed')
            }
          },
          error_callback: (error) => {
            // console.error('Google Identity Services error:', error)
            setError('Google sign-in failed: ' + error.type)
          }
        })

        // Render the Google Sign-In button
        const googleButton = document.getElementById('google-signin-button')
        if (googleButton) {
          window.google.accounts.id.renderButton(googleButton, {
            theme: 'outline',
            size: 'large',
            width: 300, // Use fixed width instead of percentage
            text: 'signin_with'
          })
        }
      } catch (error) {
        // console.error('Google sign-in initialization error:', error)
      }
    }

    initializeGoogleSignIn()

    // Cleanup function
    return () => {
      // Clean up Google button if it exists
      const googleButton = document.getElementById('google-signin-button')
      if (googleButton && window.google?.accounts?.id) {
        googleButton.innerHTML = ''
      }
    }
  }, [googleSignIn, onClose])

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      let result
      if (isSignUp) {
        result = await register(formData.username, formData.email, formData.password)
      } else {
        result = await login(formData.email, formData.password)
      }

      if (result.success) {
        onClose()
      } else {
        setError(result.error)
      }
    } catch (error) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }


  return (
    <div className="w-full max-w-md aspect-[4/3] flex flex-col justify-center">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Points FYI</h1>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          {isSignUp ? 'Sign Up' : 'Sign In'}
        </h2>
      </div>

      {/* Form */}
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="space-y-3">
          {isSignUp && (
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required={isSignUp}
                value={formData.username}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Username"
              />
            </div>
          )}
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Email Address"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                required
                value={formData.password}
                onChange={handleInputChange}
                className="block w-full pr-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="text-red-600 text-sm text-center">
            {error}
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : (isSignUp ? 'Sign Up' : 'Sign In')}
          </button>
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-primary-600 hover:text-primary-500 text-sm"
          >
            {isSignUp ? 'Have an account already? Sign In Here' : "Don't have an account? Sign Up Here"}
          </button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">OR</span>
          </div>
        </div>

        <div className="flex justify-center">
          <div id="google-signin-button"></div>
        </div>
      </form>

      <div className="text-center text-xs text-gray-500 mt-4">
        By signing up or signing in, you are agreeing to Points FYI's{' '}
        <a href="#" className="text-primary-600 hover:text-primary-500">
          Terms of Use
        </a>{' '}
        and{' '}
        <a href="#" className="text-primary-600 hover:text-primary-500">
          Privacy Policy
        </a>
        .
      </div>
    </div>
  )
}

export default AuthForm
